/**
 * Users API
 */
import { apiClient } from "./client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "./auth";

export interface User {
    id: string;
    userName: string;
    email: string;
    primaryTeamId?: string;
    primaryTeamName?: string;
    isApproved: boolean;
    lastLoginAt?: string;
}

export interface UserListParams {
    search?: string;
    teamId?: string;
    role?: string;
    page?: number;
    pageSize?: number;
}

export interface UserUpdateRequest {
    email?: string;
    primaryTeamId?: string;
    isApproved?: boolean;
}

export interface MunicipalityWithTeam {
    municipalityPolygonId: string;
    name: string;
    code: string;
    teamId: string;
}

export interface TeamIdName {
    id: string;
    name: string;
}

export interface SetUserRoleRequest {
    role: string;
    teamId: string;
}

export interface UserTeamContext {
    role: string;
    teamType: string;
    countyId?: string;
    municipalityPolygonId?: string;
}

export interface UserRole {
    teamMembershipId: string;
    teamId: string;
    teamName: string;
    role: string;
    isGuest: boolean;
    isPrimary: boolean;
}

export interface UserRoleRequest {
    role: string;
    teamId?: string;
    isGuest?: boolean;
}

/**
 * Fetches all approved users (distributors)
 */
export const fetchUsers = async (params?: UserListParams): Promise<User[]> => {
    const response = await apiClient.get<User[]>("/users", { params });
    return response.data;
};

/**
 * Fetches the national team (CenterReach Nation) for SuperAdmin/RiksAdmin role assignment.
 */
export const fetchNationalTeam = async (): Promise<TeamIdName> => {
    const response = await apiClient.get<TeamIdName>("/users/national-team");
    return response.data;
};

/**
 * Fetches distrikt teams for the given county.
 */
export const fetchDistriktTeams = async (countyId: string): Promise<TeamIdName[]> => {
    const response = await apiClient.get<TeamIdName[]>("/users/distrikt-teams", {
        params: { countyId },
    });
    return response.data;
};

/**
 * Gets user's role and team context for initializing the edit form.
 */
export const fetchUserTeamContext = async (id: string): Promise<UserTeamContext> => {
    const response = await apiClient.get<UserTeamContext>(`/users/${id}/team-context`);
    return response.data;
};

/**
 * Sets the user's single role and team. Replaces all existing memberships.
 */
export const setUserRole = async (
    id: string,
    request: SetUserRoleRequest
): Promise<void> => {
    await apiClient.put(`/users/${id}/role`, request);
};

/**
 * Fetches municipalities (kommuner) with team IDs for primary team (krets) selection in user form.
 */
export const fetchMunicipalitiesForPrimaryTeam = async (
    countyId?: string | null
): Promise<MunicipalityWithTeam[]> => {
    const params = countyId ? { countyId } : {};
    const response = await apiClient.get<MunicipalityWithTeam[]>("/users/municipalities-for-primary-team", {
        params,
    });
    return response.data;
};

/**
 * Fetches a single user by ID
 */
export const fetchUser = async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
};

/**
 * Updates a user
 */
export const updateUser = async (id: string, request: UserUpdateRequest): Promise<User> => {
    const response = await apiClient.put<User>(`/users/${id}`, request);
    return response.data;
};

/**
 * Approves a user
 */
export const approveUser = async (id: string): Promise<User> => {
    const response = await apiClient.put<User>(`/users/${id}/approve`);
    return response.data;
};

/**
 * Deletes (soft delete) a user
 */
export const deleteUser = async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
};

/**
 * Fetches user roles
 */
export const fetchUserRoles = async (id: string): Promise<UserRole[]> => {
    const response = await apiClient.get<UserRole[]>(`/users/${id}/roles`);
    return response.data;
};

/**
 * Adds a role to a user
 */
export const addUserRole = async (id: string, request: UserRoleRequest): Promise<UserRole> => {
    const response = await apiClient.post<UserRole>(`/users/${id}/roles`, request);
    return response.data;
};

/**
 * Removes a role from a user
 */
export const removeUserRole = async (id: string, roleId: string): Promise<void> => {
    await apiClient.delete(`/users/${id}/roles/${roleId}`);
};

/**
 * React Query hook for fetching users
 */
export const useUsers = (params?: UserListParams) => {
    return useQuery({
        queryKey: ["users", params],
        queryFn: () => fetchUsers(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * React Query hook for fetching the national team
 */
export const useNationalTeam = () => {
    return useQuery({
        queryKey: ["users", "national-team"],
        queryFn: fetchNationalTeam,
        staleTime: 10 * 60 * 1000, // 10 minutes - rarely changes
    });
};

/**
 * React Query hook for fetching distrikt teams by county
 */
export const useDistriktTeams = (countyId?: string | null) => {
    return useQuery({
        queryKey: ["users", "distrikt-teams", countyId ?? "none"],
        queryFn: () => fetchDistriktTeams(countyId!),
        enabled: !!countyId,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * React Query hook for fetching user team context
 */
export const useUserTeamContext = (id: string) => {
    return useQuery({
        queryKey: ["users", id, "team-context"],
        queryFn: () => fetchUserTeamContext(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * React Query hook for fetching the current user's team context.
 * Returns role, teamType, countyId, municipalityPolygonId when user is in a Krets/Avdelning team.
 */
export const useCurrentUserTeamContext = () => {
    const { data: currentUser } = useCurrentUser();
    return useUserTeamContext(currentUser?.id ?? "");
};

/**
 * React Query hook for setting user role
 */
export const useSetUserRole = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, ...request }: { id: string } & SetUserRoleRequest) =>
            setUserRole(id, request),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["users", variables.id, "roles"] });
            queryClient.invalidateQueries({ queryKey: ["users", variables.id] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
    });
};

/**
 * React Query hook for fetching municipalities for primary team selection
 */
export const useMunicipalitiesForPrimaryTeam = (countyId?: string | null) => {
    return useQuery({
        queryKey: ["users", "municipalities-for-primary-team", countyId ?? "all"],
        queryFn: () => fetchMunicipalitiesForPrimaryTeam(countyId ?? undefined),
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * React Query hook for fetching a single user
 */
export const useUser = (id: string) => {
    return useQuery({
        queryKey: ["users", id],
        queryFn: () => fetchUser(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * React Query hook for updating a user
 */
export const useUpdateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, ...request }: { id: string } & UserUpdateRequest) =>
            updateUser(id, request),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.setQueryData(["users", data.id], data);
        },
    });
};

/**
 * React Query hook for approving a user
 */
export const useApproveUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: approveUser,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.setQueryData(["users", data.id], data);
        },
    });
};

/**
 * React Query hook for deleting a user
 */
export const useDeleteUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
    });
};

/**
 * React Query hook for fetching user roles
 */
export const useUserRoles = (id: string) => {
    return useQuery({
        queryKey: ["users", id, "roles"],
        queryFn: () => fetchUserRoles(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * React Query hook for adding a role to a user
 */
export const useAddUserRole = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, ...request }: { id: string } & UserRoleRequest) =>
            addUserRole(id, request),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["users", variables.id, "roles"] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
    });
};

/**
 * React Query hook for removing a role from a user
 */
export const useRemoveUserRole = () => {
    const queryClient = useQueryClient(); return useMutation({
        mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
            removeUserRole(userId, roleId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["users", variables.userId, "roles"] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
    });
};